import { formatDuration, safeToLocaleTimeString } from '@utils/date.ts';
import { useTranslation } from 'react-i18next';
import cn from 'classnames';
import { useSharedCaption } from '@utils/providers/shared-caption-context.tsx';
import { Tooltip } from '@components/atoms/ui-kit/tooltip.tsx';
import { SessionDatesTooltip } from '@components/editors/shared/session-dates-tooltip.tsx';
import Logo from '@components/shared/logo.tsx';
import {
  AlertCircle,
  Check,
  FileText,
  Globe,
  Loader2,
  Save,
  Timer
} from 'lucide-react';
import { useThemeVars } from '@utils/hooks/use-theme-vars.ts';
import { useThemeContext } from '@utils/hooks/use-theme-context.ts';
import { SharedCaptionExportDocument } from '@components/editors/shared-editor/shared-caption/shared-caption-export-document.tsx';

const SharedCaptionAbout = () => {
  const { t } = useTranslation();

  const {
    documentResult: document,
    session,
    document: captionDocument,
    saveStatus,
    lastLocalSave,
    lastServerSync
  } = useSharedCaption();

  const { vars, cls } = useThemeVars();
  const { getColour } = useThemeContext();

  const allowExportFlag =
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session as any)?.allow_magic_link_export ??
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    (session as any)?.allow_magic_link_share;

  const backendReady = allowExportFlag !== undefined;
  const allowExport = allowExportFlag ?? false;

  const getSaveStatusInfo = () => {
    switch (saveStatus) {
      case 'synced':
        return {
          text: t('status.synced'),
          color: 'text-green-600',
          bgColor: 'bg-green-100',
          icon: <Check className="inline-block w-3.5 h-3.5" />,
          timestamp: lastServerSync
            ? safeToLocaleTimeString(lastServerSync)
            : null
        };
      case 'saved-locally':
        return {
          text: t('status.saved-locally'),
          color: 'text-yellow-600',
          bgColor: 'bg-yellow-100',
          icon: <Save className="inline-block w-3.5 h-3.5" />,
          timestamp: lastLocalSave
            ? safeToLocaleTimeString(lastLocalSave)
            : null
        };
      case 'saving':
        return {
          text: t('status.saving'),
          color: 'text-blue-600',
          bgColor: 'bg-blue-100',
          icon: <Loader2 className="inline-block w-3.5 h-3.5 animate-spin" />,
          timestamp: null
        };
      case 'unsaved':
      default:
        return {
          text: t('status.unsaved'),
          color: 'text-red-600',
          bgColor: 'bg-red-100',
          icon: <AlertCircle className="inline-block w-3.5 h-3.5" />,
          timestamp: null
        };
    }
  };

  const statusInfo = getSaveStatusInfo();

  const currentLanguage = (
    document?.result?.language || session?.language
  )?.toUpperCase();

  const liveCharacterCount =
    captionDocument?.document?.reduce(
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (acc: number, seg: any) => acc + (seg?.text?.length ?? 0),
      0
    ) ?? 0;

  return (
    <div className="p-4 pb-0.5 lg:gap-0.5" style={vars}>
      <div
        className={cn('rounded-t-md')}
        style={{
          backgroundColor: getColour('baseLayer')
        }}
      >
        <div
          className={cn(
            'm-2 px-3 md:px-4 py-2',
            'grid grid-cols-8 gap-3 items-stretch border rounded-xl',
            'shadow-[0_0_10px_0px_rgba(0,0,0,0.06),0_3px_8px_rgba(0,0,0,0.05)]',
            cls.text,
            cls.border
          )}
          style={{ backgroundColor: getColour('secondLayer') }}
        >
          {/* Col 1: Logo — TopNav is hidden on editor routes, so the About
              card is where the brand mark lives while editing. */}
          <div className="col-span-1 flex items-center justify-center border-r border-slate-200 pr-3">
            <Logo isLink />
          </div>
          {/* Cols 2–8: original two-row content, unchanged. */}
          <div className="col-span-7 flex flex-col w-full divide-y divide-[var(--divide)]">
            {/* Top row: filename */}
            <div className="flex-1 min-w-0 pb-2">
              <div className="flex items-center gap-2 min-w-0">
                <h1 className="text-base md:text-lg font-semibold leading-none truncate max-w-[200px] sm:max-w-xs md:max-w-lg m-0">
                  {document?.result?.filename || session?.filename}
                </h1>
              </div>
            </div>

            {/* Lower row: metadata + toolbar */}
            <div className="pl-2 pt-2 flex flex-col md:flex-row md:justify-between md:items-center gap-3 md:gap-6">
              <div className="pl-1 flex items-center flex-wrap gap-2 md:gap-3">
                {currentLanguage && (
                  <div className="flex items-center gap-1">
                    <Globe
                      size={12}
                      className="shrink-0"
                      style={{ color: getColour('icon') }}
                    />
                    <span
                      className="text-xs uppercase tracking-wider font-medium"
                      style={{ color: getColour('inactiveText') }}
                    >
                      {currentLanguage}
                    </span>
                  </div>
                )}
                {session?.duration && (
                  <div className="flex items-center gap-1">
                    <Timer
                      size={12}
                      className="shrink-0"
                      style={{ color: getColour('icon') }}
                    />
                    <span
                      className="text-xs tabular-nums"
                      style={{ color: getColour('normalText') }}
                    >
                      {formatDuration(session.duration)}
                    </span>
                  </div>
                )}
                <div className="flex items-center gap-1">
                  <FileText
                    size={12}
                    className="shrink-0"
                    style={{ color: getColour('icon') }}
                  />
                  <span
                    className="text-xs tabular-nums"
                    style={{ color: getColour('normalText') }}
                  >
                    {liveCharacterCount} characters
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  <span
                    className="text-xs uppercase tracking-wider font-medium hidden sm:inline"
                    style={{ color: getColour('inactiveText') }}
                  >
                    {t('status.label')}
                  </span>
                  <Tooltip
                    label={
                      statusInfo.timestamp
                        ? `${t('status.last-saved')}: ${statusInfo.timestamp}`
                        : statusInfo.text
                    }
                  >
                    <span
                      className={cn(
                        'text-xs font-medium px-1.5 py-0.5 rounded-full flex items-center gap-0.5 md:gap-1',
                        statusInfo.color,
                        statusInfo.bgColor
                      )}
                    >
                      {statusInfo.icon}
                      <span className="hidden xs:inline sm:inline">
                        {statusInfo.text}
                      </span>
                    </span>
                  </Tooltip>
                  <SessionDatesTooltip
                    dateUpload={session?.date_upload}
                    dateSessionStart={session?.date_session_start}
                    dateFinished={session?.date_finished}
                  />
                </div>
              </div>

              {/* Right side of the lower row — translate / export / finish
                  buttons, mirrors the private caption-about's export cluster. */}
              <div className="flex items-center">
                <SharedCaptionExportDocument
                  allowExport={allowExport}
                  backendReady={backendReady}
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SharedCaptionAbout;
